<script setup lang="ts">
    import { Listbox, ListboxButton, ListboxLabel } from '@headlessui/vue';
    import type { FormSelectOption } from '~/components/atm/FormSelectOptions.vue';
    import type { FormSelectOptionsValueBase } from '~/components/atm/FormSelectOptionsValue.vue';

    export interface FormSelectProps extends FormSelectOptionsValueBase {
        options: FormSelectOption[];
        label?: string;
        forId?: string;
        required?: boolean;
        hidden?: boolean;
        error?: string;
        multiple?: boolean;
        labelBackground?: string;
        name?: string;
        size?: 'small' | 'medium';
        teleport?: boolean;
        hideOverlay?: boolean;
        hideSelectValue?: boolean;
        isCrankset?: boolean;
    }

    const props = withDefaults(defineProps<FormSelectProps>(), {
        label: '',
        labelBackground: 'bg-white',
        name: '',
        error: '',
        size: 'medium',
    });

    // Still need to define the emit manually for the onMounted workaround
    const emit = defineEmits(['update:dropdownValue']);
    const selectedOption = defineModel<any[] | string>('dropdownValue', { required: true, default: [] });

    const { t } = useI18n();

    const optionsTop = ref(0);
    const optionsLeft = ref(0);
    const optionsWidth = ref(0);
    const currentValueDisabled = ref(false);

    const selectedOptionString = computed(() => (Array.isArray(selectedOption.value) ? selectedOption.value.join(',') : selectedOption.value));

    const selectValue = computed(() => {
        // display all selected values
        if (props.multiple && Array.isArray(selectedOption.value)) {
            return selectedOption.value
                .map((selected: string) => {
                    const found = props.options.find((option) => option.value === selected);
                    return found?.label || found?.value || selected;
                })
                .join(', ');
        }

        // display the matching label or the value if labels are not provided
        const matchingValue = props.options.find((option) => {
            return option.value === selectedOption.value;
        });

        if (matchingValue?.label) {
            return Array.isArray(matchingValue?.label) ? matchingValue?.label.join(', ') : matchingValue?.label;
        } else {
            return Array.isArray(matchingValue?.value) ? matchingValue?.value.join(', ') : matchingValue?.value;
        }
    });

    const labelValue = computed(() => {
        return props.label ? t(props.label) : t('forms.validation.select');
    });

    const handleOpenClick = (event: MouseEvent) => {
        const dropdownButton = event.currentTarget as HTMLElement;
        const { top, left, height, width } = dropdownButton.getBoundingClientRect();
        // 2px border needs to be added manually
        optionsTop.value = window.scrollY + top + height + 2;
        optionsLeft.value = left;
        optionsWidth.value = width;
    };

    watch(
        () => props.options,
        (newValue) => {
            currentValueDisabled.value = newValue.some((option) => option.value === selectValue.value && option.disabled?.value);
        },
        { deep: true, immediate: true },
    );

    onMounted(() => {
        if (selectedOption.value.length) emit('update:dropdownValue', selectedOption.value);
    });
</script>

<template>
    <div :class="['mol-form-select mb-5 flex flex-col text-woom-black', { hidden, 'text-woom-red': !!error }]">
        <listbox
            v-slot="{ open }"
            v-model="selectedOption"
            :multiple="multiple"
            as="div">
            <div class="relative">
                <div class="relative">
                    <listbox-label
                        :class="[
                            'pointer-events-none absolute left-1 top-1/2 z-10 origin-[0] -translate-y-1/2 transform px-2 text-inherit duration-300',
                            labelBackground === 'bg-transparent' ? 'bg-white' : labelBackground,
                            {
                                'top-2 -translate-y-4 scale-75': (open || selectValue?.length) && !hideSelectValue,
                                'text-sm': size === 'small',
                            },
                        ]">
                        <span :class="{ 'opacity-60': !hideSelectValue }">{{ labelValue }}</span>
                        <lazy-atm-form-required v-if="required" />
                    </listbox-label>
                    <!-- FIXME: THIS IS NOT PRETTY, but currently there seems to be no way to make "required" work in a HTML form context -->
                    <input
                        class="sr-only pointer-events-none absolute bottom-0 right-0 w-full"
                        type="text"
                        aria-hidden="true"
                        tabindex="-1"
                        :name="forId"
                        :required="required"
                        :value="selectedOptionString" />

                    <listbox-button
                        :class="[
                            'flex w-full flex-grow appearance-none items-center gap-2 rounded border border-solid border-current p-4 pr-8 text-inherit ring-current transition-shadow focus:outline-none focus:ring-1 ',
                            {
                                'ring-1 ': open,
                                'text-woom-grey-moon': currentValueDisabled,
                                'h-14': size === 'medium',
                                'h-10 text-sm': size === 'small',
                            },
                        ]"
                        @click="handleOpenClick">
                        <atm-form-select-options-value
                            v-if="!hideSelectValue"
                            :option="{ value: selectedOptionString, label: selectValue, disabled: ref(currentValueDisabled) }"
                            :translate="translate"
                            :prefixComponent="prefixComponent"
                            :postfixComponent="postfixComponent"
                            :isCrankset="isCrankset"
                            class="w-full" />

                        <woom-icon-arrow-down
                            :class="[
                                'pointer-events-none absolute right-4 top-1/2 h-6 w-6 -translate-y-1/2 fill-current',
                                { ' rotate-180': open },
                            ]" />
                    </listbox-button>
                </div>
                <span
                    v-if="!!error"
                    class="font-mono text-xs">
                    {{ error }}
                </span>
                <div v-if="open">
                    <teleport
                        v-if="teleport"
                        to="#vue-root">
                        <atm-form-select-options
                            :open="open"
                            :options="options"
                            :options-width="optionsWidth"
                            :options-top="optionsTop"
                            :options-left="optionsLeft"
                            :translate="translate"
                            :option-multi-line="optionMultiLine"
                            teleport />
                    </teleport>
                    <atm-form-select-options
                        v-else
                        :open="open"
                        :options="options"
                        :hide-overlay="hideOverlay"
                        :translate="translate"
                        :option-multi-line="optionMultiLine"
                        :prefixComponent="prefixComponent"
                        :postfixComponent="postfixComponent" />
                </div>
            </div>
        </listbox>
    </div>
</template>
